/**
 * RescheduleModal - Allows customer to pick new date/time for booking
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { getApiBase } from '../../lib/apiBase';

const API_BASE = getApiBase();

interface BookingInfo {
  id: string;
  scheduledAt: string;
  vehicle: { registration: string; make: string; model: string } | null;
}

interface RescheduleModalProps {
  booking: BookingInfo;
  onClose: () => void;
  onSuccess: () => void;
}

export function RescheduleModal({ booking, onClose, onSuccess }: RescheduleModalProps) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionId = localStorage.getItem('customer_session');

  const handleSubmit = async () => {
    if (!newDate || !newTime || !sessionId) return;

    const scheduledAt = Math.floor(new Date(`${newDate}T${newTime}`).getTime() / 1000);

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/customer-portal/bookings/${booking.id}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Customer-Session': sessionId,
        },
        body: JSON.stringify({ scheduledAt }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Error al reprogramar');
        return;
      }

      onSuccess();
    } catch {
      setError('Error de conexion');
    } finally {
      setSubmitting(false);
    }
  };

  // Min date = tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Reprogramar Cita</h2>

        {booking.vehicle && (
          <p className="text-sm text-gray-600 mb-4">
            🚗 {booking.vehicle.registration} - {booking.vehicle.make} {booking.vehicle.model}
          </p>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva Fecha</label>
            <input
              type="date"
              min={minDate}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva Hora</label>
            <input
              type="time"
              min="08:00"
              max="18:00"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !newDate || !newTime}>
            {submitting ? 'Reprogramando...' : 'Confirmar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
